import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Check, Loader2, Coins } from "lucide-react";

export default function PublicPricing() {
  const [catalog, setCatalog] = useState(null);
  const [annual, setAnnual] = useState(false);

  useEffect(() => {
    api.get("/billing/catalog").then(({ data }) => setCatalog(data)).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bp-grid">
      <Navbar />
      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-8">
        <div className="text-center">
          <p className="font-mono2 text-xs uppercase tracking-[0.2em] text-primary">Pricing</p>
          <h1 className="mt-2 font-heading text-4xl font-extrabold tracking-tighter sm:text-5xl">Simple, honest pricing</h1>
          <p className="mx-auto mt-3 max-w-lg text-muted-foreground">
            Start free. Upgrade for unlimited projects, AI re-estimates and blueprint saves — or buy credits as you go.
          </p>
        </div>

        <div className="mt-8 flex items-center justify-center gap-3">
          <span className={`font-mono2 text-xs uppercase tracking-widest ${!annual ? "text-primary" : "text-muted-foreground"}`}>Monthly</span>
          <Switch checked={annual} onCheckedChange={setAnnual} data-testid="public-interval-toggle" />
          <span className={`font-mono2 text-xs uppercase tracking-widest ${annual ? "text-primary" : "text-muted-foreground"}`}>
            Annual <span className="text-accent">(2 months free)</span>
          </span>
        </div>

        {!catalog ? (
          <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
        ) : (
          <>
            <div className="mt-10 grid gap-4 md:grid-cols-3">
              {catalog.plans.map((p, i) => {
                const price = annual ? p.price_yearly : p.price_monthly;
                const highlight = p.id === "pro";
                return (
                  <motion.div key={p.id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                    className={`flex flex-col border p-6 ${highlight ? "border-primary" : "border-border"}`} data-testid={`public-plan-${p.id}`}>
                    <div className="flex items-center justify-between">
                      <h3 className="font-heading text-xl font-bold">{p.name}</h3>
                      {highlight && <span className="bg-primary px-2 py-0.5 font-mono2 text-[10px] uppercase tracking-widest text-black">Popular</span>}
                    </div>
                    <div className="mt-4 font-mono2">
                      <span className="text-4xl font-bold text-foreground">${price}</span>
                      <span className="text-sm text-muted-foreground">{p.id === "free" ? "" : annual ? " / yr" : " / mo"}</span>
                    </div>
                    <ul className="mt-6 flex-1 space-y-2.5">
                      {p.features.map((f) => (
                        <li key={f} className="flex items-start gap-2 text-sm text-muted-foreground">
                          <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" /> {f}
                        </li>
                      ))}
                    </ul>
                    <Link to="/register" className="mt-6" data-testid={`public-cta-${p.id}`}>
                      <Button className={`w-full rounded-sm font-mono2 uppercase tracking-widest ${
                        highlight ? "bg-primary text-black hover:bg-primary/90" : "border border-border bg-transparent hover:bg-secondary"}`}>
                        {p.id === "free" ? "Start Free" : "Get Started"}
                      </Button>
                    </Link>
                  </motion.div>
                );
              })}
            </div>

            <div className="mt-14">
              <div className="mb-4 flex items-center gap-2">
                <Coins className="h-4 w-4 text-primary" />
                <h2 className="font-mono2 text-xs uppercase tracking-widest text-muted-foreground">One-time estimate credits</h2>
              </div>
              <div className="grid gap-4 sm:grid-cols-2">
                {catalog.credit_packs.map((c) => (
                  <div key={c.id} className="flex items-center justify-between border border-border p-5">
                    <div>
                      <p className="font-heading text-lg font-semibold">{c.name}</p>
                      <p className="font-mono2 text-xs text-muted-foreground">Each credit adds one extra project on the Free plan.</p>
                    </div>
                    <span className="font-mono2 text-2xl font-bold text-primary">${c.price}</span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </section>
      <Footer />
    </div>
  );
}
